import { SubformsInputBlock as SubformsInputBlockType } from '@coast/schemas';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useContext, useRef, useState } from 'react';
import { View as RNView, StyleSheet } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBIconV2 } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextSubtitle } from '@shared/components/typography';
import { Subform } from '@shared/data/entity/fields/SubformField';
import { SubformContent } from '@shared/scenes/entity/components/fields/Subform/SubformsContent';
import { SubformsSheet } from '@shared/scenes/entity/components/fields/Subform/SubformsSheet';
import { useWorkflowTemplate, useWorkspaceDetails } from '@shared/scenes/workflows/hooks';
import { isAuditTagComponent } from '@shared/scenes/workflows/utils';
import { ColorThemeContext, getAnchorName, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { MBAlert } from '@shared/util/alert';
import { i18n } from '@shared/util/i18n';
import { BaseSubformRoute, useBuilderLink, useNavStackContext } from '@shared/util/navigation';
import { CardChannelContext } from '../../contexts/channel';
import { useCardFormContext } from '../../contexts/form';
import { MBBlockLabel } from '../../utils/label';

interface Props {
  block: SubformsInputBlockType;
  value: unknown;
  workflowTemplateId?: string | null;
}

export function SubformsInputBlock({ block, value, workflowTemplateId }: Props) {
  const { setFieldValue } = useCardFormContext();
  const channel = useContext(CardChannelContext);
  const colors = useContext(ColorThemeContext);
  const workspaceDetails = useWorkspaceDetails();
  const [sheetOpen, setSheetOpen] = useState(false);
  const containerRef = useRef<RNView>(null);
  const parentWorkflowTemplateId = workflowTemplateId ?? channel?.workflowTemplateId ?? null;
  const { workflowTemplate } = useWorkflowTemplate(parentWorkflowTemplateId ?? undefined);
  const hasSubform =
    typeof value === 'object' &&
    value !== null &&
    'workflowTemplateId' in value &&
    typeof (value as Subform).workflowTemplateId === 'string';
  const nav = useNavStackContext<BaseSubformRoute>();
  const channelSlug = workspaceDetails.get(parentWorkflowTemplateId ?? '')?.baseNavigationPath;
  const navigateToBuilder = useBuilderLink(parentWorkflowTemplateId ?? undefined, channelSlug);
  const anchorName = getAnchorName('subforms-input-block', block.name);
  const isReadOnly = block.readOnly ?? false;

  const onPressContainer = useCallback(() => {
    setSheetOpen(true);
  }, []);

  const onSelectSubform = useCallback(
    (subformWorkflowTemplate: API.WorkflowTemplateFragment) => {
      const componentsWithDefaults =
        subformWorkflowTemplate.components
          ?.filter(isAuditTagComponent)
          .filter(c => !isEmpty(c.default?.value)) ?? [];

      const defaultData = componentsWithDefaults.reduce(
        (acc, component) => {
          acc[component.id] = { tags: component.default?.value ?? [] };
          return acc;
        },
        {} as Record<string, { tags: unknown[] }>,
      );

      setFieldValue(block.name, {
        workflowTemplateId: subformWorkflowTemplate.id,
        data: defaultData,
      });
      setSheetOpen(false);
    },
    [block.name, setFieldValue],
  );

  const onSelect = useCallback(
    (subformWftId: string) => {
      nav.push({
        name: 'subform',
        params: {
          workflowTemplateId: subformWftId,
          parentComponentId: block.name,
        },
      });
      setSheetOpen(false);
    },
    [block.name, nav],
  );

  const onClear = useCallback(() => {
    MBAlert.confirm({
      message: i18n('card.blocks.subformsInput.confirmClear'),
      confirmText: i18n('common.ok'),
      onConfirm: () => {
        setFieldValue(block.name, null);
      },
    });
  }, [block.name, setFieldValue]);

  const navigateToAdd = useCallback(() => {
    navigateToBuilder({ route: '/subform', isNested: true, includeMenu: false });
  }, [navigateToBuilder]);

  if (isNil(parentWorkflowTemplateId)) {
    return null;
  }

  const canOpenSheet = !hasSubform && !isReadOnly;
  const showAddRow = !isReadOnly && !isNil(channelSlug);

  return (
    <MBTouchableOpacity onPress={canOpenSheet ? onPressContainer : undefined}>
      <View style={{ anchorName }}>
        {block.label && (
          <View paddingLeft={3} paddingVertical={1}>
            <MBBlockLabel label={block.label} />
          </View>
        )}
        {hasSubform && (
          <RNView
            ref={containerRef}
            style={{ paddingHorizontal: grid(1), alignContent: 'center', alignItems: 'center' }}
          >
            <SubformContent
              subform={{
                workflowTemplateId: (value as Subform).workflowTemplateId,
                data: (value as Subform).data ?? {},
              }}
              onSelect={onSelect}
              onClear={onClear}
              isReadOnly={isReadOnly}
            />
          </RNView>
        )}
        {!hasSubform && (
          <View paddingHorizontal={1}>
            <View style={{ anchorName }}>
              <MBView
                paddingVertical={2}
                center
                style={[
                  styles.container,
                  {
                    backgroundColor: colors.backgroundInactive,
                    borderColor: colors.input.borderInactive,
                  },
                ]}
              >
                <View row centerVertically>
                  <MBIconV2
                    color={colors.foregroundActive}
                    size={grid(5)}
                    name={block.icon || 'list'}
                  />
                  <View paddingLeft={1} />
                  <MBTextSubtitle color={colors.foregroundActive}>
                    {block.placeholder}
                  </MBTextSubtitle>
                </View>
              </MBView>
            </View>
          </View>
        )}
      </View>
      {sheetOpen && (
        <SubformsSheet
          anchorName={anchorName}
          blockLabel={block.label ?? ''}
          workflowTemplate={workflowTemplate}
          onClose={() => setSheetOpen(false)}
          onSelect={onSelectSubform}
          navigateToAdd={showAddRow ? navigateToAdd : undefined}
        />
      )}
    </MBTouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: grid(1),
    borderStyle: 'dashed',
    borderWidth: 2,
  },
});
